import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Leaf, Sprout, ShoppingCart, Wallet, AlertTriangle, TrendingUp } from "lucide-react";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";

const LOGO = `${process.env.PUBLIC_URL}/logo.jpeg`;

const FEATURES = [
  { icon: ShoppingCart, label: "POS & Invoicing" },
  { icon: Wallet, label: "Credit / Khata" },
  { icon: AlertTriangle, label: "Stock Alerts" },
  { icon: TrendingUp, label: "Profit & Growth" },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-agri-bg">
      {/* ---- Brand hero (logo colours + animation) ---- */}
      <div className="hidden lg:flex relative overflow-hidden">
        {/* animated brand gradient: green -> lime -> gold -> red (the logo's ring) */}
        <div className="absolute inset-0 bg-[linear-gradient(125deg,#0f5a27_0%,#2f9e2f_32%,#f5b301_66%,#e01f26_100%)] bg-[length:300%_300%] animate-gradient-pan" />
        <div className="absolute inset-0 bg-agri-forest/45" />

        {/* decorative rotating rings */}
        <div className="absolute -right-28 -top-28 h-96 w-96 rounded-full border-[10px] border-white/10 animate-spin-slow" />
        <div className="absolute -left-20 -bottom-16 h-72 w-72 rounded-full border-[8px] border-white/10 animate-spin-slow" />
        <Leaf size={42} className="absolute top-24 right-40 text-white/25 animate-float" />
        <Sprout size={34} className="absolute bottom-40 right-28 text-white/20 animate-float-slow" />

        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3 animate-fade-up">
            <img src={LOGO} alt="ZaaM Agro Services" className="h-12 w-12 rounded-xl bg-white object-contain p-1 shadow-lg" />
            <div>
              <div className="font-heading text-xl leading-none">ZaaM Agro Services</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/80 mt-1">Protecting Crops, Securing Future</div>
            </div>
          </div>

          <div className="animate-fade-up">
            {/* big logo medallion with rotating ring */}
            <div className="relative w-44 h-44 mb-8">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/30 animate-spin-slow" />
              <div className="absolute inset-2 rounded-full bg-white shadow-2xl grid place-items-center animate-float">
                <img src={LOGO} alt="ZaaM Agro Services" className="h-32 w-32 object-contain" />
              </div>
            </div>
            <h1 className="font-heading text-4xl xl:text-5xl leading-tight">Grow your shop.<br />Track every grain.</h1>
            <p className="mt-4 text-white/85 max-w-md text-sm leading-relaxed">
              The complete POS, inventory, credit-ledger and accounts suite built for
              pesticide, fertilizer &amp; seed retailers.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {FEATURES.map((f) => (
                <span key={f.label} className="inline-flex items-center gap-1.5 text-xs bg-white/15 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
                  <f.icon size={13} /> {f.label}
                </span>
              ))}
            </div>
          </div>

          <div className="text-xs text-white/60">© {new Date().getFullYear()} ZaaM Agro Services</div>
        </div>
      </div>

      {/* ---- Sign-in ---- */}
      <div className="flex items-center justify-center p-6 lg:p-12 relative">
        {/* subtle brand accent bar on top */}
        <div className="absolute top-0 inset-x-0 h-1.5 bg-[linear-gradient(90deg,#0f5a27,#4caf2f,#f5b301,#e01f26)]" />
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO} alt="ZaaM Agro Services" className="h-12 w-12 rounded-xl bg-white object-contain p-1 shadow-sm border border-black/5" />
            <div>
              <div className="font-heading text-lg leading-none">ZaaM Agro Services</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-agri-ink-2 mt-1">Protecting Crops, Securing Future</div>
            </div>
          </div>

          <div className="label-cap mb-2">Sign In</div>
          <h2 className="font-heading text-3xl text-agri-ink">Welcome back</h2>
          <p className="text-sm text-agri-ink-2 mt-1.5">Sign in to access your shop dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block mb-1.5 text-sm font-medium text-agri-ink">Email</label>
              <input
                data-testid="login-email-input"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full border border-[rgba(28,28,30,0.18)] bg-white rounded-md px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-agri-leaf/30 focus:border-agri-leaf"
              />
            </div>
            <div>
              <label className="block mb-1.5 text-sm font-medium text-agri-ink">Password</label>
              <input
                data-testid="login-password-input"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full border border-[rgba(28,28,30,0.18)] bg-white rounded-md px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-agri-leaf/30 focus:border-agri-leaf"
              />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-agri-danger bg-red-50 border border-red-100 rounded-lg p-3">{err}</div>}
            <button
              data-testid="login-submit-btn"
              type="submit" disabled={loading}
              className="w-full bg-[linear-gradient(90deg,#0f5a27,#2f9e2f)] hover:brightness-110 active:scale-[0.98] text-white font-medium rounded-lg px-4 py-2.5 transition-all shadow-sm disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
